import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { AccountService } from '../../../services/account.service';
import { SegmentService } from '../../../services/segment.service';
import { Account, Segment } from '../../../interfaces/accounting.interface';
import { BudgetLine, BudgetVersion, FpaService, VersionCreateRequest, XlsxImportResult } from '../../../services/fpa.service';
import SearchableSelect from '../../common/SearchableSelect';
import BudgetGrid from './BudgetGrid';
import { GridRow, fiscalMonths, linesToRows, monthSpan, rowsToLines, windowOf } from './budgetGridUtils';
import { currentFiscalYearStartLocalIso } from '../../../utils/dateUtils';
import { InstitutionService } from '../../../services/institution.service';
import { AuthService } from '../../../services/auth.service';
import { useDepartmentOptions } from '../../costcentre/DepartmentPicker'; // B4-H
import { useProjectOptions } from '../../project/ProjectSelect'; // P1-A project

const statusColor = (s: string) => (s === 'LOCKED' ? 'default' : s === 'APPROVED' ? 'success' : 'warning') as 'default' | 'success' | 'warning';

type Mode = 'create' | 'legacy' | 'roll' | null;

const BudgetVersions: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.BUDGET_MANAGE);
    const [versions, setVersions] = useState<BudgetVersion[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const [selected, setSelected] = useState<BudgetVersion | null>(null);
    const [rows, setRows] = useState<GridRow[]>([]);
    const [tagged, setTagged] = useState<BudgetLine[]>([]);
    const [monthCount, setMonthCount] = useState(12);
    const [dirty, setDirty] = useState(false);
    const [mode, setMode] = useState<Mode>(null);
    const [busy, setBusy] = useState(false);
    const [importResult, setImportResult] = useState<XlsxImportResult | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    // create-dialog form
    // the institution's current fiscal-year start (not today): "New version" and "Import legacy" default to it
    const [fyStart, setFyStart] = useState(() => currentFiscalYearStartLocalIso(1));
    const [form, setForm] = useState<VersionCreateRequest>({ name: '', fiscalYearStart: currentFiscalYearStartLocalIso(1), kind: 'ORIGINAL', mode: 'BLANK' });
    const [rollBase, setRollBase] = useState('');

    const months = useMemo(() => (selected ? fiscalMonths(selected.fiscalYearStart, monthCount) : []), [selected, monthCount]);

    const loadVersions = useCallback(async () => {
        try { setVersions(await FpaService.listVersions()); } catch (err) { toast.showApiError(err, 'Failed to load budget versions'); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadVersions();
        const institutionId = AuthService.getSelectedInstitution();
        if (institutionId) {
            InstitutionService.getInstitutionById(institutionId)
                .then(i => setFyStart(currentFiscalYearStartLocalIso(i?.fiscalYearStartMonth ?? 1)))
                .catch(() => undefined);   // keep the January default
        }
        AccountService.getAllAccounts().then(setAccounts).catch(err => toast.showApiError(err, 'Failed to load accounts'));
        SegmentService.getByInstitution(0, 200).then(p => setSegments(p.content ?? p)).catch(err => toast.showApiError(err, 'Failed to load segments'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const open = async (v: BudgetVersion) => {
        if (dirty && !window.confirm('Discard unsaved changes to the open version?')) return;
        try {
            const lines = await FpaService.getLines(v.id);
            // The grid shows every month the version stores (a forecast runs past 12), so a save never drops hidden ones.
            const count = monthSpan(v.fiscalYearStart, lines);
            const g = linesToRows(lines, fiscalMonths(v.fiscalYearStart, count));
            setSelected(v); setMonthCount(count); setRows(g.rows); setTagged(g.tagged); setDirty(false);
        } catch (err) { toast.showApiError(err, 'Failed to load the budget lines'); }
    };

    const refreshSelected = async (v: BudgetVersion) => {
        await loadVersions();
        await open(v);
    };

    const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
        try { setBusy(true); await fn(); toast.showSuccess(ok); return true; }
        catch (err) { toast.showApiError(err, fail); return false; }
        finally { setBusy(false); }
    };

    const submitCreate = async () => {
        let created: BudgetVersion | null = null;
        const ok = await run(async () => {
            if (mode === 'legacy') created = await FpaService.importLegacy(form.name, form.fiscalYearStart);
            else if (mode === 'roll') created = await FpaService.rollForward(rollBase, form.name, form.scenario);
            else created = await FpaService.createVersion(form);
        }, 'Version created', 'Could not create the version');
        if (ok) { setMode(null); await loadVersions(); if (created) await open(created); }
    };

    const save = async () => {
        if (!selected) return;
        const { lines, errors } = rowsToLines(rows, months, tagged);
        if (errors.length) { toast.showError(errors.slice(0, 3).join('; ')); return; }
        const win = windowOf(months);
        const ok = await run(() => FpaService.saveLines(selected.id, lines, win.from, win.to), 'Lines saved', 'Could not save the lines');
        if (ok) { setDirty(false); await open(selected); }
    };

    const transition = async (fn: (id: string) => Promise<BudgetVersion>, ok: string) => {
        if (!selected) return;
        let v: BudgetVersion | null = null;
        const good = await run(async () => { v = await fn(selected.id); }, ok, 'Action failed');
        if (good && v) { setDirty(false); await refreshSelected(v); }
    };

    const remove = async () => {
        if (!selected || !window.confirm(`Delete draft "${selected.name}"?`)) return;
        const ok = await run(() => FpaService.deleteVersion(selected.id), 'Draft deleted', 'Could not delete');
        if (ok) { setSelected(null); setRows([]); await loadVersions(); }
    };

    const pickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f || !selected) return;
        try { setPendingFile(f); setImportResult(await FpaService.importXlsx(selected.id, f, true)); }
        catch (err) { toast.showApiError(err, 'Could not read the workbook'); setPendingFile(null); }
    };

    const commitImport = async () => {
        if (!selected || !pendingFile) return;
        const ok = await run(() => FpaService.importXlsx(selected.id, pendingFile, false), 'Workbook imported', 'Import failed');
        if (ok) { setImportResult(null); setPendingFile(null); await open(selected); }
    };

    const editable = !!selected && selected.status === 'DRAFT' && canManage;
    const dialogTitle = mode === 'legacy' ? 'Import legacy budgets' : mode === 'roll' ? 'Rolling forecast' : 'New budget version';
    const canSubmit = form.name.trim() !== '' && (mode !== 'roll' || rollBase !== '') && (mode !== 'create' || form.mode !== 'COPY_VERSION' || !!form.sourceVersionId);

    return (
        <Box data-help-page="accounting.budgetVersions.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Budget Versions</Typography>
            {canManage && (
                <Box data-help-section="accounting.budgetVersions.toolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                    <Button data-help="accounting.budgetVersions.new" variant="contained" onClick={() => { setForm({ name: '', fiscalYearStart: fyStart, kind: 'ORIGINAL', mode: 'BLANK' }); setMode('create'); }}>New version</Button>
                    <Button data-help="accounting.budgetVersions.legacy" variant="outlined" onClick={() => { setForm({ name: 'FY Original', fiscalYearStart: fyStart, kind: 'ORIGINAL', mode: 'BLANK' }); setMode('legacy'); }}>Import legacy budgets</Button>
                    <Button data-help="accounting.budgetVersions.roll" variant="outlined" onClick={() => { setForm({ name: '', fiscalYearStart: fyStart, kind: 'REVISED', mode: 'BLANK' }); setRollBase(''); setMode('roll'); }}>Rolling forecast</Button>
                </Box>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table data-help-section="accounting.budgetVersions.table" size="small">
                    <TableHead><TableRow><TableCell data-help="accounting.budgetVersions.col.name">Name</TableCell><TableCell data-help="accounting.budgetVersions.col.kind">Kind</TableCell><TableCell data-help="accounting.budgetVersions.col.fiscalYear">Fiscal year start</TableCell><TableCell data-help="accounting.budgetVersions.col.scenario">Scenario</TableCell><TableCell data-help="accounting.budgetVersions.col.status">Status</TableCell></TableRow></TableHead>
                    <TableBody>
                        {versions.map(v => (
                            <TableRow key={v.id} hover selected={selected?.id === v.id} sx={{ cursor: 'pointer' }} onClick={() => open(v)}>
                                <TableCell>{v.name}</TableCell><TableCell>{v.kind}</TableCell><TableCell>{v.fiscalYearStart}</TableCell>
                                <TableCell>{v.scenario ?? ''}</TableCell>
                                <TableCell><Chip size="small" label={v.status} color={statusColor(v.status)} /></TableCell>
                            </TableRow>
                        ))}
                        {versions.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No budget versions yet.</Typography></TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            {selected && (
                <Box>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="h6">{selected.name}</Typography>
                        <Chip size="small" label={selected.status} color={statusColor(selected.status)} />
                        {selected.status !== 'DRAFT' && <Typography variant="body2" color="text.secondary">Read-only{selected.status === 'APPROVED' ? ' - reopen to edit' : ''}</Typography>}
                    </Stack>
                    {canManage && (
                        <Box data-help-section="accounting.budgetVersions.editorToolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                            {editable && <Button data-help="accounting.budgetVersions.action.addRow" variant="outlined" onClick={() => { setRows([...rows, { key: `new-${rows.length}-${Date.now()}`, accountId: '', segmentId: '', amounts: Array(months.length).fill('') }]); setDirty(true); }}>Add row</Button>}
                            {editable && <Button data-help="accounting.budgetVersions.action.save" variant="contained" disabled={busy || !dirty} onClick={save}>Save lines</Button>}
                            <Button data-help="accounting.budgetVersions.action.template" variant="outlined" onClick={() => FpaService.downloadTemplate(selected.id).catch(err => toast.showApiError(err, 'Download failed'))}>Excel template</Button>
                            {editable && <Button data-help="accounting.budgetVersions.action.import" variant="outlined" onClick={() => fileRef.current?.click()}>Import Excel</Button>}
                            <input ref={fileRef} aria-label="Budget workbook" type="file" accept=".xlsx" hidden onChange={pickFile} data-testid="xlsx-input" />
                            {selected.status === 'DRAFT' && <Button data-help="accounting.budgetVersions.action.approve" color="success" variant="outlined" disabled={busy || dirty} onClick={() => transition(FpaService.approve, 'Version approved')}>Approve</Button>}
                            {selected.status === 'DRAFT' && selected.kind !== 'FORECAST' && (
                                <Button data-help="accounting.budgetVersions.action.changeKind" variant="outlined" disabled={busy || dirty}
                                    onClick={() => transition(id => FpaService.changeKind(id, selected.kind === 'REVISED' ? 'ORIGINAL' : 'REVISED'),
                                        selected.kind === 'REVISED' ? 'Version is now an original budget' : 'Version is now a revision')}>
                                    {selected.kind === 'REVISED' ? 'Make original' : 'Make a revision'}
                                </Button>
                            )}
                            {selected.status === 'APPROVED' && <Button data-help="accounting.budgetVersions.action.lock" variant="outlined" disabled={busy} onClick={() => transition(FpaService.lock, 'Version locked')}>Lock</Button>}
                            {selected.status === 'APPROVED' && <Button data-help="accounting.budgetVersions.action.reopen" variant="outlined" disabled={busy} onClick={() => transition(FpaService.reopen, 'Version reopened')}>Reopen</Button>}
                            {selected.status === 'DRAFT' && <Button data-help="accounting.budgetVersions.action.delete" color="error" variant="outlined" disabled={busy} onClick={remove}>Delete draft</Button>}
                        </Box>
                    )}
                    {selected.status === 'DRAFT' && selected.createdByMe && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>Approval needs a different user from the creator (maker-checker).</Alert>
                    )}
                    {tagged.length > 0 && <Alert severity="info" sx={{ mb: 1.5 }}>{tagged.length} order-tagged line(s) are kept as-is and are not shown in this grid.</Alert>}
                    <BudgetGrid rows={rows} months={months} accounts={accounts} segments={segments} departments={departmentOptions} projects={projectOptions} readOnly={!editable}
                        onChange={r => { setRows(r); setDirty(true); }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Tip: copy a block of cells in Excel and paste into any month cell; extra rows are appended (choose their accounts afterwards).
                    </Typography>
                </Box>
            )}

            <Dialog data-help-page="accounting.budgetVersions.form.page" open={mode !== null} onClose={() => setMode(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField data-help="accounting.budgetVersions.form.field.name" label="Name" size="small" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        {mode !== 'roll' && (
                            <TextField data-help="accounting.budgetVersions.form.field.fiscalYearStart" label="Fiscal year start" type="date" size="small" value={form.fiscalYearStart}
                                onChange={e => setForm({ ...form, fiscalYearStart: e.target.value })} InputLabelProps={{ shrink: true }} />
                        )}
                        {mode === 'legacy' && <Alert severity="info">Copies the existing Budgets of that fiscal year into a new ORIGINAL version.</Alert>}
                        {mode === 'roll' && (
                            <>
                                <SearchableSelect data-help="accounting.budgetVersions.form.field.base" options={versions.filter(v => v.status !== 'DRAFT')} value={rollBase} onChange={setRollBase}
                                    getOptionLabel={v => `${v.name} (${v.status})`} label="Base version" size="small" />
                                <Alert severity="info">Actuals replace the base for closed months; open months keep the base figure. Creates a FORECAST version.</Alert>
                            </>
                        )}
                        {mode === 'create' && (
                            <>
                                <TextField data-help="accounting.budgetVersions.form.field.kind" select label="Kind" size="small" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value as 'ORIGINAL' | 'REVISED' })}>
                                    <MenuItem value="ORIGINAL">Original</MenuItem><MenuItem value="REVISED">Revised</MenuItem>
                                </TextField>
                                <TextField data-help="accounting.budgetVersions.form.field.startFrom" select label="Start from" size="small" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value as VersionCreateRequest['mode'] })}>
                                    <MenuItem value="BLANK">Blank</MenuItem>
                                    <MenuItem value="COPY_VERSION">Copy of another version</MenuItem>
                                    <MenuItem value="LAST_YEAR_ACTUALS">Last year's actuals</MenuItem>
                                </TextField>
                                {form.mode === 'COPY_VERSION' && (
                                    <SearchableSelect data-help="accounting.budgetVersions.form.field.copyFrom" options={versions} value={form.sourceVersionId ?? ''} onChange={id => setForm({ ...form, sourceVersionId: id || undefined })}
                                        getOptionLabel={v => `${v.name} (${v.status})`} label="Copy from" size="small" />
                                )}
                                {form.mode !== 'BLANK' && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        <TextField data-help="accounting.budgetVersions.form.field.adjustPct" label="Adjust %" type="number" size="small" value={form.adjustPct ?? ''} sx={{ flex: '1 1 140px', maxWidth: 200 }}
                                            onChange={e => setForm({ ...form, adjustPct: e.target.value === '' ? undefined : Number(e.target.value) })} />
                                        <TextField data-help="accounting.budgetVersions.form.field.adjustScope" select label="Adjust scope" size="small" value={form.adjustScope ?? 'ALL'} sx={{ flex: '1 1 160px', maxWidth: 220 }}
                                            onChange={e => setForm({ ...form, adjustScope: e.target.value as 'ALL' | 'REVENUE' | 'EXPENSE' })}>
                                            <MenuItem value="ALL">All accounts</MenuItem><MenuItem value="REVENUE">Revenue only</MenuItem><MenuItem value="EXPENSE">Expense only</MenuItem>
                                        </TextField>
                                    </Box>
                                )}
                            </>
                        )}
                        <TextField data-help="accounting.budgetVersions.form.field.scenario" label="Scenario (optional)" size="small" value={form.scenario ?? ''} onChange={e => setForm({ ...form, scenario: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.budgetVersions.form.cancel" onClick={() => setMode(null)}>Cancel</Button>
                    <Button data-help="accounting.budgetVersions.form.create" variant="contained" disabled={!canSubmit || busy} onClick={submitCreate}>Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog data-help-page="accounting.budgetVersions.import.page" open={importResult !== null} onClose={() => { setImportResult(null); setPendingFile(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>Import preview (dry run)</DialogTitle>
                <DialogContent>
                    {importResult && (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <Typography>{importResult.rowsRead} rows read, {importResult.linesAccepted} lines accepted.</Typography>
                            {importResult.errors.length > 0 && (
                                <Alert severity="error">{importResult.errors.slice(0, 10).map(e => <div key={e}>{e}</div>)}{importResult.errors.length > 10 && <div>...and {importResult.errors.length - 10} more</div>}</Alert>
                            )}
                            {importResult.errors.length === 0 && <Alert severity="success">No problems found. Importing replaces this draft's grid lines (order-tagged lines are kept).</Alert>}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.budgetVersions.import.cancel" onClick={() => { setImportResult(null); setPendingFile(null); }}>Cancel</Button>
                    <Button data-help="accounting.budgetVersions.import.commit" variant="contained" disabled={busy || !importResult || importResult.errors.length > 0 || importResult.linesAccepted === 0} onClick={commitImport}>Import</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BudgetVersions;
